import React, { useState, useEffect } from 'react';
import { useStudentAuth } from '../../context/StudentAuthContext';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

const MyProfile = () => {
  const { studentApi } = useStudentAuth();
  const { user } = useStudentAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [academicRecords, setAcademicRecords] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState({});
  const [filePreviews, setFilePreviews] = useState({}); // For previewing newly selected files

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const [profileRes, gradesRes, fieldsRes] = await Promise.all([
        studentApi.get('/student-portal/profile'),
        studentApi.get('/student-portal/grades'),
        studentApi.get('/student-portal/student-fields')
      ]);
      setProfile(profileRes.data);
      setAcademicRecords(gradesRes.data || []);
      setCustomFields(fieldsRes.data);
    } catch (error) {
      toast.error('Failed to load profile data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const profileSchema = Yup.object().shape({
    surname: Yup.string().required('Surname is required'),
    othernames: Yup.string().required('Other names are required'),
    sex: Yup.string().oneOf(['Male', 'Female', 'Other']),
    dob: Yup.date().required('Date of birth is required'),
    address: Yup.string(),
    guardian: Yup.string(),
    guardian_address: Yup.string(),
    guardian_phone: Yup.string(),
  });

  const passwordSchema = Yup.object().shape({
    current_password: Yup.string().required('Current password is required'),
    new_password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('New password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .required('Please confirm your password'),
  });

  useEffect(() => {
    fetchProfileData();
  }, [user]);

  const handleFileUpload = async (fieldCode, file) => {
    if (!file) return;

    // Find field validation rules
    const field = customFields.find(f => f.field_code === fieldCode);
    if (!field) return;

    const validationRules = field.validation_rules || {};
    const allowedExtensions = validationRules.allowed_extensions || ['jpg', 'jpeg', 'png', 'pdf'];
    const maxSizeMB = validationRules.max_file_size_mb || 5;
    const fileExt = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(fileExt)) {
      toast.error(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File too large. Max size: ${maxSizeMB} MB`);
      return;
    }

    // Generate preview for image files
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviews(prev => ({ ...prev, [fieldCode]: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreviews(prev => ({ ...prev, [fieldCode]: null }));
    }

    const formData = new FormData();
    formData.append('file', file);
    setFileUploading(prev => ({ ...prev, [fieldCode]: true }));
    try {
      const res = await studentApi.post(`/student-portal/upload-custom-field-file?field_code=${fieldCode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Update customFields state with new URL
      setCustomFields(prev => prev.map(f =>
        f.field_code === fieldCode ? { ...f, value: res.data.file_url } : f
      ));
      toast.success('File uploaded successfully');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Upload failed';
      toast.error(msg);
      // Clear preview on error
      setFilePreviews(prev => ({ ...prev, [fieldCode]: null }));
    } finally {
      setFileUploading(prev => ({ ...prev, [fieldCode]: false }));
    }
  };

  const handleUpdateProfile = async (values, { setSubmitting }) => {
    try {
      // Prepare custom fields payload (excluding file fields)
      const customPayload = {};
      customFields.forEach(field => {
        if (field.field_type !== 'file') {
          customPayload[field.field_code] = field.value;
        } else {
          // Include file URL if exists
          if (field.value) {
            customPayload[field.field_code] = field.value;
          }
        }
      });
      const payload = {
        ...values,
        custom_fields: customPayload
      };
      await studentApi.put('/student-portal/update-profile', payload);
      toast.success('Profile updated successfully');
      setShowEditModal(false);
      // Clear previews
      setFilePreviews({});
      // Refresh profile data to reflect changes
      fetchProfileData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (values, { setSubmitting, resetForm }) => {
    try {
      await studentApi.post('/auth/student/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get full image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `https://multi-schoolsuite.cbthost.com${path}`;
  };

  if (loading && !profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Get passport URL from custom_fields
  const passportUrl = profile?.custom_fields?.passport ? getImageUrl(profile.custom_fields.passport) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
          <p className="text-gray-600">View and manage your personal information</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="btn-primary flex items-center"
          >
            <i className="fas fa-edit mr-2"></i> Edit Profile
          </button>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="btn-secondary flex items-center"
          >
            <i className="fas fa-key mr-2"></i> Change Password
          </button>
        </div>
      </div>

      {/* Profile Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Information */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
              <p className="text-gray-600 text-sm">Your personal details and contact information</p>
            </div>
            <div className="text-sm text-gray-500">
              Student ID: {profile?.student_reg || '--'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Full Name</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.surname} {profile?.othernames}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.dob ? format(new Date(profile.dob), 'dd/MM/yyyy') : '--'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Gender</label>
                <div className="mt-1">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {profile?.sex || '--'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Age</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.dob ? new Date().getFullYear() - new Date(profile.dob).getFullYear() : '--'} years
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information - Custom Fields */}
          {customFields.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {customFields.map(field => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-500">
                      {field.display_name}
                    </label>
                    <div className="mt-1">
                      {field.field_type === 'file' && field.value && (
                        field.value.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img
                            src={getImageUrl(field.value)}
                            alt={field.display_name}
                            className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <a
                            href={getImageUrl(field.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline"
                          >
                            <i className="fas fa-file-alt mr-1"></i> View File
                          </a>
                        )
                      )}
                      {field.field_type === 'boolean' && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${field.value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {field.value ? 'Yes' : 'No'}
                        </span>
                      )}
                      {field.field_type === 'select' && field.options && (
                        <div className="text-gray-900">{field.value || 'Not set'}</div>
                      )}
                      {['text', 'number', 'date'].includes(field.field_type) && (
                        <div className="text-gray-900">{field.value || 'Not set'}</div>
                      )}
                      {field.field_type === 'file' && !field.value && (
                        <div className="text-gray-400">No file uploaded</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Profile Summary Card with Passport Image */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 overflow-hidden bg-primary-100">
              {passportUrl ? (
                <img
                  src={passportUrl}
                  alt="Passport"
                  className="w-full h-full object-cover"
                />
              ) : (
                <i className="fas fa-user-graduate text-primary-600 text-3xl"></i>
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{profile?.surname} {profile?.othernames}</h3>
            <p className="text-gray-500">Student</p>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Student ID:</span>
              <span className="font-medium">{profile?.student_reg || '--'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Status:</span>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Enrolled:</span>
              <span className="font-medium">
                {profile?.created_at ? format(new Date(profile.created_at), 'dd/MM/yyyy') : '--'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Class:</span>
              <span className="font-medium">{profile?.class_name || 'Not assigned'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Academic Records */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Academic Records</h3>
              <p className="text-gray-600 text-sm">Your academic performance history</p>
            </div>
            <div className="text-sm text-gray-500">
              {academicRecords.length} records
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Academic Session</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Term</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Average Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {academicRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.academic_session}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{record.class}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{record.term}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.position}</td>
                  <td className="px-6 py-4">
                    <div className={`text-sm font-bold ${
                      record.average >= 90 ? 'text-green-600' :
                      record.average >= 80 ? 'text-green-500' :
                      record.average >= 70 ? 'text-blue-500' :
                      record.average >= 60 ? 'text-yellow-500' :
                      'text-red-500'
                    }`}>
                      {record.average.toFixed(1)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      record.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.comment}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setFilePreviews({});
        }}
        title="Edit Profile"
        size="lg"
      >
        <Formik
          initialValues={{
            surname: profile?.surname || '',
            othernames: profile?.othernames || '',
            sex: profile?.sex || 'Male',
            dob: profile?.dob ? format(new Date(profile.dob), 'yyyy-MM-dd') : '',
            address: profile?.address || '',
            guardian: profile?.guardian || '',
            guardian_address: profile?.guardian_address || '',
            guardian_phone: profile?.guardian_phone || '',
          }}
          validationSchema={profileSchema}
          onSubmit={handleUpdateProfile}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surname *</label>
                  <Field type="text" name="surname" className="input-field" />
                  {errors.surname && touched.surname && <div className="text-red-500 text-sm mt-1">{errors.surname}</div>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Names *</label>
                  <Field type="text" name="othernames" className="input-field" />
                  {errors.othernames && touched.othernames && <div className="text-red-500 text-sm mt-1">{errors.othernames}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <Field as="select" name="sex" className="input-field">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Field>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <Field type="date" name="dob" className="input-field" />
                  {errors.dob && touched.dob && <div className="text-red-500 text-sm mt-1">{errors.dob}</div>}
                </div>
              </div>

              {/* Dynamic Custom Fields */}
              {customFields.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Additional Information</h4>
                  <div className="space-y-4">
                    {customFields.map(field => {
                      const currentValue = field.value || '';
                      const previewUrl = filePreviews[field.field_code];
                      const existingUrl = field.value ? getImageUrl(field.value) : null;
                      const displayUrl = previewUrl || existingUrl;
                      const isImage = field.field_type === 'file' && displayUrl && (displayUrl.match(/\.(jpg|jpeg|png|gif)$/i) || displayUrl.startsWith('data:image'));

                      if (field.field_type === 'file') {
                        return (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-gray-700">
                              {field.display_name} {field.is_compulsory && '*'}
                            </label>
                            {displayUrl && (
                              <div className="mb-3">
                                {isImage ? (
                                  <div className="relative inline-block">
                                    <img
                                      src={displayUrl}
                                      alt={field.display_name}
                                      className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                                    />
                                    {previewUrl && (
                                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full px-1">
                                        New
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <a
                                    href={displayUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 text-sm"
                                  >
                                    <i className="fas fa-file-alt mr-1"></i> Current file
                                  </a>
                                )}
                              </div>
                            )}
                            <input
                              type="file"
                              accept={(() => {
                                const rules = field.validation_rules || {};
                                const exts = rules.allowed_extensions || [];
                                return exts.map(ext => `.${ext}`).join(',');
                              })()}
                              onChange={(e) => handleFileUpload(field.field_code, e.target.files[0])}
                              disabled={fileUploading[field.field_code]}
                              className="input-field"
                            />
                            {fileUploading[field.field_code] && (
                              <p className="text-sm text-blue-500 mt-1">Uploading...</p>
                            )}
                            {field.validation_rules?.max_file_size_mb && (
                              <p className="text-xs text-gray-500 mt-1">
                                Max size: {field.validation_rules.max_file_size_mb} MB
                              </p>
                            )}
                          </div>
                        );
                      }

                      if (field.field_type === 'select' && field.options) {
                        const options = Array.isArray(field.options) ? field.options : field.options.split(',');
                        return (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-gray-700">
                              {field.display_name} {field.is_compulsory && '*'}
                            </label>
                            <select
                              value={currentValue}
                              onChange={(e) => {
                                const newVal = e.target.value;
                                setCustomFields(prev => prev.map(f =>
                                  f.field_code === field.field_code ? { ...f, value: newVal } : f
                                ));
                              }}
                              className="input-field"
                            >
                              <option value="">Select {field.display_name}</option>
                              {options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        );
                      }

                      if (field.field_type === 'boolean') {
                        return (
                          <div key={field.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`edit_field_${field.field_code}`}
                              checked={currentValue === true || currentValue === 'true'}
                              onChange={(e) => {
                                const newVal = e.target.checked;
                                setCustomFields(prev => prev.map(f =>
                                  f.field_code === field.field_code ? { ...f, value: newVal } : f
                                ));
                              }}
                              className="mr-2"
                            />
                            <label htmlFor={`edit_field_${field.field_code}`} className="text-sm text-gray-700">
                              {field.display_name}
                            </label>
                          </div>
                        );
                      }

                      // text, number, date
                      const inputType = field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text';
                      return (
                        <div key={field.id}>
                          <label className="block text-sm font-medium text-gray-700">
                            {field.display_name} {field.is_compulsory && '*'}
                          </label>
                          <input
                            type={inputType}
                            value={currentValue}
                            onChange={(e) => {
                              let newVal = e.target.value;
                              if (field.field_type === 'number') newVal = parseFloat(newVal);
                              setCustomFields(prev => prev.map(f =>
                                f.field_code === field.field_code ? { ...f, value: newVal } : f
                              ));
                            }}
                            className="input-field"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setFilePreviews({});
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Change Password"
      >
        <Formik
          initialValues={{
            current_password: '',
            new_password: '',
            confirm_password: '',
          }}
          validationSchema={passwordSchema}
          onSubmit={handleChangePassword}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password *</label>
                <Field type="password" name="current_password" className="input-field" />
                {errors.current_password && touched.current_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.current_password}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <Field type="password" name="new_password" className="input-field" />
                {errors.new_password && touched.new_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.new_password}</div>
                )}
                <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password *</label>
                <Field type="password" name="confirm_password" className="input-field" />
                {errors.confirm_password && touched.confirm_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.confirm_password}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Changing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-key mr-2"></i>
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default MyProfile;
